import React, { useState, FocusEvent, useId } from "react";
import { observer } from "mobx-react";
import classNames from "classnames";
import styles from "./Input.scss";
import rootStyles from "~views/pages/Root.scss";
import { IInfoPopoverProps } from "../InfoPopover/InfoPopover";
import errorIcon from "~assets/error.svg";
import { LabelRow } from "../LabelRow/LabelRow";

type IInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  icon?: string;
  suffix?: string;
  valueSuffix?: string;
  className?: string;
  wrapperStyle?: string;
  value?: string;
  infoPopover?: IInfoPopoverProps;
  error?: string;
  floatingPlaceholder?: string;
  isNumeric?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
};

export const Input = observer(
  ({
    label,
    icon,
    suffix,
    valueSuffix,
    className,
    wrapperStyle,
    value,
    infoPopover,
    onBlur,
    onFocus,
    disabled,
    error,
    floatingPlaceholder,
    placeholder,
    isNumeric,
    ...props
  }: IInputProps) => {
    const [focus, setFocus] = useState(false);
    const id = useId();

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
      setFocus(false);
    };

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
      onFocus?.(e);
      setFocus(true);
    };

    return (
      <div
        className={classNames(wrapperStyle, {
          [styles.faded]: disabled,
        })}
      >
        <LabelRow label={label} infoPopover={infoPopover} htmlFor={id} />
        <div
          className={classNames(styles.wrapper, className, {
            [styles.focus]: focus,
            [styles.disabled]: disabled,
          })}
        >
          <input
            {...props}
            id={id}
            className={styles.input}
            placeholder={placeholder}
            aria-label={label ? undefined : placeholder || floatingPlaceholder}
            aria-disabled={disabled}
            readOnly={disabled}
            value={valueSuffix && !focus ? value + valueSuffix : value}
            onFocus={(e) => handleFocus(e)}
            onBlur={(e) => handleBlur(e)}
            inputMode={isNumeric ? "numeric" : "text"}
          />
          {floatingPlaceholder && (
            <span
              className={classNames(styles.placeholder, {
                [styles.float]: value || focus,
              })}
            >
              {floatingPlaceholder}
            </span>
          )}
          {icon && <img className={styles.icon} src={icon} alt="" />}
          {suffix && <span className={styles.suffix}>{suffix}</span>}
        </div>
        {error && (
          <div className={rootStyles.error}>
            <img src={errorIcon} alt="error-icon" />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }
);
